import { Input, Modal, Form, Radio, Card, Row, Col, DatePicker, Select, Space, InputNumber, Avatar, List, Tag, Collapse, Checkbox, Tooltip, Button } from "antd"
import TimePicker from "../../common/TimePicker"
import DurationPicker from "../../common/DurationPicker"
import { Duration, durationToHmm } from "../../common/durationModel"
import { useState, useEffect } from "react"
import dayjs from "dayjs"
import { useUpdateRouteMutation } from "../routingQueries"
import { CloseCircleOutlined } from "@ant-design/icons"
import { CrewMember, EndType, Route, RoutingType } from "../routingApi"

export type Props = {
  isOpen: boolean
  onClose: (routeId?: number) => void
  crew: CrewMember[] | undefined
  route: Route
  onSave?: (routeId: number) => void
  onShowRegenerateModal?: (pendingRouteUpdate: Route) => void
}

const UpdateRouteModal = ({ isOpen, onClose, crew, route, onSave, onShowRegenerateModal }: Props) => {
  const [routeType, setRouteType] = useState<'onDemand' | 'scheduled'>('scheduled')
  const [endType, setEndType] = useState<'never' | 'on' | 'after'>('on')
  const [selectedCrew, setSelectedCrew] = useState<any[]>([])
  const [crewLeadId, setCrewLeadId] = useState<number | null>(null)
  const [dayOfWeek, setDayOfWeek] = useState<string>('Sunday')
  const [workDaySettingsExpanded, setWorkDaySettingsExpanded] = useState<boolean>(false)
  const [form] = Form.useForm()
  const updateRouteMutation = useUpdateRouteMutation()

  const capitalizeDayName = (day: string): string => {
    if (!day) return 'Sunday'
    return day.charAt(0).toUpperCase() + day.slice(1).toLowerCase()
  }

  useEffect(() => {
    if (isOpen && route) {
      const crewMemberIds = route.crewMemberIds ? route.crewMemberIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id)) : []
      const routeCrewMembers = crewMemberIds.map(id => crew?.find(member => member.id === id)).filter(Boolean) as CrewMember[]

      setSelectedCrew(routeCrewMembers)
      setCrewLeadId(route.crewLeadId)

      const routeTypeValue = route.routeType === 0 ? 'onDemand' : 'scheduled'
      setRouteType(routeTypeValue)

      const scheduleConfig = route.defaultScheduleConfig
      let endTypeValue: 'never' | 'on' | 'after' = 'on'
      if (scheduleConfig?.endType === 'never') endTypeValue = 'never'
      else if (scheduleConfig?.endType === 'after') endTypeValue = 'after'
      setEndType(endTypeValue)

      const dayOfWeekValue = capitalizeDayName(scheduleConfig?.dayOfWeek || 'Sunday')
      setDayOfWeek(dayOfWeekValue)

      const parseTimeFromDateTime = (datetimeString: string) => {
        const hour = parseInt(datetimeString.split('T')[1].split('+')[0].split('Z')[0].split(':')[0])
        const minute = parseInt(datetimeString.split('T')[1].split('+')[0].split('Z')[0].split(':')[1])
        if (isNaN(hour) || isNaN(minute)) {
          return null
        }
        return dayjs().hour(hour).minute(minute)
      }

      const dayStart = route.dayStartTime ? parseTimeFromDateTime(route.dayStartTime) : dayjs('08:00', 'HH:mm')
      const dayEnd = route.dayEndTime ? parseTimeFromDateTime(route.dayEndTime) : dayjs('16:00', 'HH:mm')
      const breakTime: Duration = { seconds: (route.estimatedBreakTimeMinutes ?? 30) * 60 }

      const repeatPlace = scheduleConfig?.repeatPlace ? scheduleConfig.repeatPlace.split(',') : ["1st"]
      const repeatQuarter = scheduleConfig?.repeatQuarter ? scheduleConfig.repeatQuarter.split(',').map(q => parseInt(q)) : [1]
      const repeatMonth = scheduleConfig?.repeatMonth ? scheduleConfig.repeatMonth.split(',').map(m => parseInt(m)) : [dayjs().month() + 1]

      const formValues = {
        routeName: route.name,
        routeType: routeTypeValue,
        dayOfWeek: dayOfWeekValue,
        startDate: scheduleConfig?.startDate ? dayjs(scheduleConfig.startDate, 'YYYY-MM-DD') : dayjs(),
        repeatUnit: scheduleConfig?.repeatUnits || 'week',
        repeatCount: scheduleConfig?.repeatCount || 1,
        repeatPlace: repeatPlace,
        repeatQuarter: repeatQuarter,
        repeatMonth: repeatMonth,
        endType: endTypeValue,
        endDate: scheduleConfig?.endDate ? dayjs(scheduleConfig.endDate, 'YYYY-MM-DD') : dayjs().add(30, 'day'),
        afterTimes: scheduleConfig?.occurrences || 1,
        dayStart: dayStart,
        anticipatedDayEnd: dayEnd,
        includeBreakTime: route.hasBreakTime,
        estimatedBreakTime: breakTime,
        dayStartDisplay: dayStart,
        anticipatedDayEndDisplay: dayEnd,
        includeBreakTimeDisplay: route.hasBreakTime,
        crewSearch: crewMemberIds
      }

      form.setFieldsValue(formValues)
    }
  }, [isOpen, route, crew, form])

  const handleClose = (routeId?: number) => {
    form.resetFields()
    setRouteType('scheduled')
    setEndType('on')
    setSelectedCrew([])
    setCrewLeadId(null)
    setDayOfWeek('Sunday')
    setWorkDaySettingsExpanded(false)
    onClose(routeId)
  }

  const handleUpdate = async () => {
    try {
      const values = await form.validateFields()


      let estimatedBreakTimeMinutes = 0
      if (values.includeBreakTime && values.estimatedBreakTime) {
        estimatedBreakTimeMinutes = Math.floor(values.estimatedBreakTime.seconds / 60)
      }

      const isOnDemand = values.routeType === 'onDemand'

      const updatedRoute: Route = {
        ...route,
        name: values.routeName,
        crewLeadId: crewLeadId,
        crewMemberIds: selectedCrew.filter(member => member?.id).map(member => member.id).join(','),
        routeType: isOnDemand ? RoutingType.OnDemand : RoutingType.Planned,
        dayStartTime: values.dayStart,
        dayEndTime: values.anticipatedDayEnd,
        estimatedBreakTimeMinutes: estimatedBreakTimeMinutes,
        hasBreakTime: values.includeBreakTime,
        defaultScheduleConfig: isOnDemand ? null : {
          dayOfWeek: values.dayOfWeek.toLowerCase(),
          startDate: values.startDate.format('YYYY-MM-DD'),
          endDate: values.endDate.format('YYYY-MM-DD'),
          endType: values.endType === 'never' ? EndType.Never : values.endType === 'on' ? EndType.On : EndType.After,
          repeatCount: values.repeatCount,
          repeatUnits: values.repeatUnit,
          occurrences: values.afterTimes,
          repeatPlace: values.repeatPlace ? values.repeatPlace.join(',') : null,
          repeatQuarter: values.repeatQuarter ? values.repeatQuarter.join(',') : null,
          repeatMonth: values.repeatMonth ? values.repeatMonth.join(',') : null,
        }
      }

      if (route.active && onShowRegenerateModal) {
        handleClose()
        onShowRegenerateModal(updatedRoute)
      } else {
        updateRouteMutation.mutate(updatedRoute, {
          onSuccess: () => {
            if (onSave) {
              onSave(route.id)
            }
            handleClose(route.id)
          }
        })
      }
    } catch (error) {
    }
  }

  const handleCrewSelect = (value: number[] | undefined) => {
    if (!value || !Array.isArray(value)) {
      setSelectedCrew([])
      return
    }

    const newCrewMembers = value.map(id =>
      crew?.find(member => member.id === id)
    ).filter(Boolean) as CrewMember[]

    setSelectedCrew(newCrewMembers)

    if (!crewLeadId && newCrewMembers.length > 0) {
      const firstWithEmail = newCrewMembers.find(member => member.userEmail)
      if (firstWithEmail) {
        setCrewLeadId(firstWithEmail.id)
      }
    }
  }

  const removeCrewMember = (id: any) => {
    const updatedCrew = selectedCrew.filter(member => member.id !== id)
    setSelectedCrew(updatedCrew)
    form.setFieldsValue({ crewSearch: updatedCrew.map(member => member.id) })
  }

  const setCrewLead = (id: number) => {
    if (crewLeadId === id) {
      setCrewLeadId(null)
    } else {
      setCrewLeadId(id)
    }
  }

  return <Modal
    open={isOpen}
    onCancel={() => handleClose()}
    onOk={handleUpdate}
    okText="Update"
    width={1000}
    title="Update Route"
  >
    <Form
      form={form}
      layout="vertical"
    >
      {/* Hidden fields to ensure values are always registered with the form */}
      <Form.Item name="dayStart" initialValue={dayjs('08:00', 'HH:mm')} hidden>
        <Input />
      </Form.Item>
      <Form.Item name="anticipatedDayEnd" initialValue={dayjs('16:00', 'HH:mm')} hidden>
        <Input />
      </Form.Item>
      <Form.Item name="includeBreakTime" initialValue={true} valuePropName="checked" hidden>
        <Input />
      </Form.Item>
      <Form.Item
        label="Route Name"
        name="routeName"
        rules={[
          { required: true, message: 'Please enter a route name' },
          { max: 150, message: 'Route name cannot exceed 150 characters' }
        ]}
      >
        <Input
          placeholder="Route Name"
          maxLength={150}
          showCount
        />
      </Form.Item>
      <Row gutter={16}>
        <Col span={13}>
          <Form.Item
            label="Route Type"
            name="routeType"
            initialValue="scheduled"
          >
            <Card>
              <Radio.Group
                value={routeType}
                onChange={(e) => setRouteType(e.target.value)}
              >
                <Radio value="onDemand">On Demand</Radio>
                <Radio value="scheduled">Planned</Radio>
              </Radio.Group>
            </Card>
          </Form.Item>

          {routeType === 'scheduled' && (
            <>
              <Form.Item
                label="Planned Weekday"
                name="dayOfWeek"
                initialValue="Sunday"
              >
                <Card style={{ padding: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Radio.Group
                      buttonStyle="solid"
                      value={dayOfWeek}
                      onChange={(e) => {
                        setDayOfWeek(e.target.value)
                        form.setFieldsValue({ dayOfWeek: e.target.value })
                      }}
                    >
                      <Space size="middle">
                        {[
                          { key: 'Sunday', label: 'S' },
                          { key: 'Monday', label: 'M' },
                          { key: 'Tuesday', label: 'T' },
                          { key: 'Wednesday', label: 'W' },
                          { key: 'Thursday', label: 'T' },
                          { key: 'Friday', label: 'F' },
                          { key: 'Saturday', label: 'S' }
                        ].map((day) => (
                          <Radio.Button
                            key={day.key}
                            value={day.key}
                            style={{
                              width: '55px',
                              height: '40px',
                              borderRadius: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '14px',
                              fontWeight: '500'
                            }}
                          >
                            {day.label}
                          </Radio.Button>
                        ))}
                      </Space>
                    </Radio.Group>
                  </div>
                </Card>
              </Form.Item>

              <Form.Item label="Default Schedule">
                <Card>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Form.Item
                      label="Start Date"
                      name="startDate"
                      style={{ marginBottom: 16 }}
                      initialValue={dayjs()}
                      rules={[
                        { required: true, message: 'Please select a start date' }
                      ]}
                    >
                      <DatePicker
                        style={{ width: '100%' }}
                        format="MMMM D, YYYY"
                      />
                    </Form.Item>

                    <div style={{ marginBottom: 16 }}>
                      <div style={{ marginBottom: 8 }}>
                        <span style={{ marginRight: 10 }}>Repeats</span>
                        <Form.Item
                          noStyle
                          name="repeatUnit"
                          initialValue="week"
                        >
                          <Select style={{ width: 200 }}>
                            <Select.Option value="week">Weekly</Select.Option>
                            <Select.Option value="month">Monthly</Select.Option>
                            <Select.Option value="quarter">Quarterly</Select.Option>
                            <Select.Option value="year">Annually</Select.Option>
                          </Select>
                        </Form.Item>
                      </div>
                      <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.repeatUnit !== currentValues.repeatUnit}>
                        {({ getFieldValue }) => {
                          const repeatUnit = getFieldValue('repeatUnit')
                          switch (repeatUnit) {
                            case 'week':
                              return (
                                <Space>
                                  Every <Form.Item
                                    noStyle
                                    name="repeatCount"
                                    initialValue={1}
                                    rules={[{ required: true, message: 'Please enter repeat count' }]}
                                  >
                                    <InputNumber min={1} style={{ width: 60 }} />
                                  </Form.Item>
                                  <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.repeatUnit !== currentValues.repeatUnit}>
                                    {({ getFieldValue }) => {
                                      const repeatUnit = getFieldValue('repeatUnit')
                                      return repeatUnit === 'week' ? 'week' : null
                                    }}
                                  </Form.Item>
                                </Space>
                              )
                            case 'month':
                              return (
                                <Space>
                                  On the <Form.Item
                                    noStyle
                                    name="repeatPlace"
                                    initialValue={["1st"]}
                                    rules={[{ required: true, message: 'Please select repeat place' }]}
                                  >
                                    <Select
                                      style={{ width: 120 }}
                                      mode="multiple"
                                      placeholder="Select"
                                      maxTagCount={3}
                                      maxTagTextLength={10}
                                    >
                                      <Select.Option value="1st">1st</Select.Option>
                                      <Select.Option value="2nd">2nd</Select.Option>
                                      <Select.Option value="3rd">3rd</Select.Option>
                                      <Select.Option value="4th">4th</Select.Option>
                                      <Select.Option value="last">Last</Select.Option>
                                    </Select>
                                  </Form.Item> {(() => {
                                    const dayMap: Record<string, string> = {
                                      'S': 'Sunday',
                                      'M': 'Monday',
                                      'T': 'Tuesday',
                                      'W': 'Wednesday',
                                      'T2': 'Thursday',
                                      'F': 'Friday',
                                      'S2': 'Saturday'
                                    }
                                    return dayMap[dayOfWeek] || dayOfWeek
                                  })()} of the month
                                </Space>
                              )
                            case 'quarter':
                              return (
                                <Space>
                                  On the <Form.Item
                                    noStyle
                                    name="repeatPlace"
                                    initialValue={["1st"]}
                                    rules={[{ required: true, message: 'Please select repeat place' }]}
                                  >
                                    <Select
                                      style={{ width: 120 }}
                                      mode="multiple"
                                      placeholder="Select"
                                      maxTagCount={3}
                                      maxTagTextLength={10}
                                    >
                                      <Select.Option value="1st">1st</Select.Option>
                                      <Select.Option value="2nd">2nd</Select.Option>
                                      <Select.Option value="3rd">3rd</Select.Option>
                                      <Select.Option value="4th">4th</Select.Option>
                                      <Select.Option value="last">Last</Select.Option>
                                    </Select>
                                  </Form.Item> {(() => {
                                    const dayMap: Record<string, string> = {
                                      'S': 'Sunday',
                                      'M': 'Monday',
                                      'T': 'Tuesday',
                                      'W': 'Wednesday',
                                      'T2': 'Thursday',
                                      'F': 'Friday',
                                      'S2': 'Saturday'
                                    }
                                    return dayMap[dayOfWeek] || dayOfWeek
                                  })()} of the <Form.Item
                                    noStyle
                                    name="repeatQuarter"
                                    initialValue={[1]}
                                    rules={[{ required: true, message: 'Please select quarter' }]}
                                  >
                                    <Select
                                      style={{ width: 120 }}
                                      mode="multiple"
                                      placeholder="Select"
                                      maxTagCount={3}
                                      maxTagTextLength={10}
                                    >
                                      <Select.Option value={1}>1st</Select.Option>
                                      <Select.Option value={2}>2nd</Select.Option>
                                      <Select.Option value={3}>3rd</Select.Option>
                                      <Select.Option value={4}>4th</Select.Option>
                                    </Select>
                                  </Form.Item> quarter
                                </Space>
                              )
                            case 'year':
                              return (
                                <Space>
                                  On the <Form.Item
                                    noStyle
                                    name="repeatPlace"
                                    initialValue={["1st"]}
                                    rules={[{ required: true, message: 'Please select repeat place' }]}
                                  >
                                    <Select
                                      style={{ width: 120 }}
                                      mode="multiple"
                                      placeholder="Select"
                                      maxTagCount={3}
                                      maxTagTextLength={10}
                                    >
                                      <Select.Option value="1st">1st</Select.Option>
                                      <Select.Option value="2nd">2nd</Select.Option>
                                      <Select.Option value="3rd">3rd</Select.Option>
                                      <Select.Option value="4th">4th</Select.Option>
                                      <Select.Option value="last">Last</Select.Option>
                                    </Select>
                                  </Form.Item> {(() => {
                                    const dayMap: Record<string, string> = {
                                      'S': 'Sunday',
                                      'M': 'Monday',
                                      'T': 'Tuesday',
                                      'W': 'Wednesday',
                                      'T2': 'Thursday',
                                      'F': 'Friday',
                                      'S2': 'Saturday'
                                    }
                                    return dayMap[dayOfWeek] || dayOfWeek
                                  })()} of <Form.Item
                                    noStyle
                                    name="repeatMonth"
                                    initialValue={[dayjs().month() + 1]}
                                    rules={[{ required: true, message: 'Please select month' }]}
                                  >
                                    <Select
                                      style={{ width: 120 }}
                                      mode="multiple"
                                      placeholder="Select"
                                      maxTagCount={3}
                                      maxTagTextLength={10}
                                    >
                                      <Select.Option value={1}>January</Select.Option>
                                      <Select.Option value={2}>February</Select.Option>
                                      <Select.Option value={3}>March</Select.Option>
                                      <Select.Option value={4}>April</Select.Option>
                                      <Select.Option value={5}>May</Select.Option>
                                      <Select.Option value={6}>June</Select.Option>
                                      <Select.Option value={7}>July</Select.Option>
                                      <Select.Option value={8}>August</Select.Option>
                                      <Select.Option value={9}>September</Select.Option>
                                      <Select.Option value={10}>October</Select.Option>
                                      <Select.Option value={11}>November</Select.Option>
                                      <Select.Option value={12}>December</Select.Option>
                                    </Select>
                                  </Form.Item>
                                </Space>
                              )
                            default:
                              return null
                          }
                        }}
                      </Form.Item>
                    </div>

                    <div>
                      <div style={{ marginBottom: 8 }}>Ends</div>
                      <Form.Item
                        noStyle
                        name="endType"
                        rules={[{ required: true, message: 'Please select end type' }]}
                        initialValue="on"
                      >
                        <Radio.Group
                          onChange={(e) => {
                            setEndType(e.target.value)
                          }}
                        >
                          <Space direction="vertical">
                            <Radio value="on">
                              On <Form.Item
                                noStyle
                                name="endDate"
                                initialValue={dayjs().add(30, 'day')}
                                rules={[
                                  ({ getFieldValue }) => ({
                                    validator(_, value) {
                                      if (endType !== 'on') {
                                        return Promise.resolve()
                                      }
                                      if (endType === 'on' && value === null) {
                                        return Promise.reject(new Error('End date is required'))
                                      }
                                      const startDate = getFieldValue('startDate')
                                      if (endType === 'on' && startDate && value && value.isBefore(startDate)) {
                                        return Promise.reject(new Error('End date cannot be before start date'))
                                      }
                                      return Promise.resolve()
                                    },
                                  }),
                                ]}
                              >
                                <DatePicker
                                  style={{ width: 200 }}
                                  format="MMMM D, YYYY"
                                  disabled={endType !== 'on'}
                                />
                              </Form.Item>
                            </Radio>
                            <Radio value="after">
                              After <Form.Item
                                noStyle
                                name="afterTimes"
                                initialValue={1}
                                rules={[
                                  () => ({
                                    validator(_, value) {
                                      if (endType !== 'after') {
                                        return Promise.resolve()
                                      }
                                      if (endType === 'after' && !value) {
                                        return Promise.reject(new Error('Please enter number of times'))
                                      }
                                      if (endType === 'after' && (!Number.isInteger(value) || value < 1)) {
                                        return Promise.reject(new Error('Must be a positive whole number'))
                                      }
                                      return Promise.resolve()
                                    },
                                  }),
                                ]}
                              >
                                <InputNumber
                                  style={{ width: 80 }}
                                  disabled={endType !== 'after'}
                                  min={1}
                                />
                              </Form.Item> times
                            </Radio>
                            {/* <Radio value="never">Never</Radio> */}
                          </Space>
                        </Radio.Group>
                      </Form.Item>
                    </div>
                  </Space>
                </Card>
              </Form.Item>
            </>
          )}
          <Form.Item label="Work Day Settings">
            <Card>
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => (
                  <Collapse
                    ghost
                    size="small"
                    activeKey={workDaySettingsExpanded ? ['workday'] : []}
                    onChange={(keys) => setWorkDaySettingsExpanded(keys.includes('workday'))}
                    expandIconPosition="end"
                    items={[
                      {
                        key: 'workday',
                        label: (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                            <div>
                              {!workDaySettingsExpanded ? (
                                <div style={{ fontSize: '14px', color: '#8c8c8c' }}>
                                  {getFieldValue('dayStart')?.format('h:mm A') || '8:00 AM'} - {getFieldValue('anticipatedDayEnd')?.format('h:mm A') || '4:00 PM'}
                                  {(getFieldValue('includeBreakTime') !== false) && (
                                    <>
                                      <br />
                                      <span style={{ color: '#8c8c8c' }}>Estimated Break Time:</span> {getFieldValue('estimatedBreakTime') ? durationToHmm(getFieldValue('estimatedBreakTime')) : '0:30'}
                                    </>
                                  )}
                                </div>
                              ) : (
                                <div style={{ fontSize: '14px', color: '#8c8c8c' }}>
                                  These settings will allow you to determine route capacity
                                </div>
                              )}
                            </div>
                          </div>
                        ),
                        children: (
                          <div>
                            <Row gutter={16}>
                              <Col span={12}>
                                <Form.Item
                                  label="Day Start"
                                  name="dayStartDisplay"
                                  initialValue={dayjs('08:00', 'HH:mm')}
                                >
                                  <TimePicker
                                    onChange={(value) => {
                                      form.setFieldsValue({ dayStart: value })
                                    }}
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item
                                  label="Anticipated Day End"
                                  name="anticipatedDayEndDisplay"
                                  initialValue={dayjs('16:00', 'HH:mm')}
                                >
                                  <TimePicker
                                    onChange={(value) => {
                                      form.setFieldsValue({ anticipatedDayEnd: value })
                                    }}
                                  />
                                </Form.Item>
                              </Col>
                            </Row>

                            <Row>
                              <Col span={24}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                  <Form.Item
                                    name="includeBreakTimeDisplay"
                                    valuePropName="checked"
                                    initialValue={true}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <Checkbox
                                      onChange={(e) => {
                                        form.setFieldsValue({ includeBreakTime: e.target.checked })
                                      }}
                                    >
                                      Estimated Break Time
                                    </Checkbox>
                                  </Form.Item>
                                  <Form.Item
                                    name="estimatedBreakTime"
                                    initialValue={{ seconds: 30 * 60 }}
                                    style={{ marginBottom: 0, flex: 1 }}
                                  >
                                    <DurationPicker increment={900} />
                                  </Form.Item>
                                </div>
                              </Col>
                            </Row>
                          </div>
                        )
                      }
                    ]}
                  />
                )}
              </Form.Item>
            </Card>
          </Form.Item>
        </Col>
        <Col span={11}>
          <Form.Item label="Crew">
            <Card>
              <Space direction="vertical" style={{ width: '100%', minHeight: 250 }}>
                <Form.Item
                  name="crewSearch"
                >
                  <Select
                    showSearch
                    placeholder="Search and select crew members"
                    optionFilterProp="label"
                    style={{ width: '100%' }}
                    onChange={handleCrewSelect}
                    value={selectedCrew?.map(member => member?.id).filter(Boolean) || []}
                    allowClear
                    mode="multiple"
                    maxTagCount={3}
                    maxTagTextLength={10}
                  >
                    {crew?.map((member) => (
                      <Select.Option
                        key={member.id}
                        value={member.id}
                        label={member.name}
                      >
                        <Space>
                          <Avatar src={member.avatar} size="small">
                            {member.name.charAt(0)}
                          </Avatar>
                          <span>{member.name}</span>
                        </Space>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                {selectedCrew.length > 0 ? (
                  <List
                    size="small"
                    dataSource={selectedCrew}
                    renderItem={(member) => (
                      <List.Item
                        actions={[
                          !member.userEmail ? (
                            <Tooltip key="setLead" title="Crew member needs an email address to be a crew lead">
                              <Button
                                type="link"
                                size="small"
                                disabled
                                style={{
                                  padding: 0,
                                  cursor: 'not-allowed'
                                }}
                              >
                                Set as Crew Lead
                              </Button>
                            </Tooltip>
                          ) : (
                            <a
                              key="setLead"
                              onClick={() => setCrewLead(member.id)}
                              style={{ color: crewLeadId === member.id ? '#faad14' : '#1890ff' }}
                            >
                              {crewLeadId === member.id ? "" : "Set as Crew Lead"}
                            </a>
                          ),
                          <a key="remove" onClick={() => removeCrewMember(member.id)}>
                            <CloseCircleOutlined style={{ fontSize: '16px' }} />
                          </a>
                        ]}
                      >
                        <List.Item.Meta
                          avatar={
                            <Avatar src={member.avatar} size="small">
                              {member.name.charAt(0)}
                            </Avatar>
                          }
                          title={
                            <Space>
                              {member.name}
                              {crewLeadId === member.id && (
                                <Tag color="gold">Crew Lead</Tag>
                              )}
                            </Space>
                          }
                        />
                      </List.Item>
                    )}
                  />
                ) : (
                  <div style={{
                    border: '1px dashed #d9d9d9',
                    borderRadius: '8px',
                    padding: '40px 0',
                    textAlign: 'center',
                    color: '#666',
                    minHeight: 250,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    No Assigned Crew
                  </div>
                )}
              </Space>
            </Card>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  </Modal>

}

export default UpdateRouteModal